import logoPodio from 'assets/img/2026/logo_podio.png';
import React from 'react';
import { FaTrophy } from 'react-icons/fa';
import { IoCheckmarkCircle, IoCloseCircle } from 'react-icons/io5';
import { SUFIXO_PREMIADA } from 'utils/iniciativa-categoria';
import './Extrato.scss';

type ExtratoVariant =
  | 'classificada-etapa1'
  | 'classificada-etapa2'
  | 'podio-1'
  | 'podio-2'
  | 'podio-3';

interface ValorBruto {
  rotulo: string;
  valor: string;
}

export interface ExtratoIniciativa {
  id: string;
  titulo: string;
  variant: ExtratoVariant;
  categoriaLabel: string;
  participacaoConfirmada: boolean;
  valores: ValorBruto[];
  /**
   * Motivo do status negativo — ainda indefinido no backend;
   * a UI não quebra enquanto o campo não existir.
   */
  statusMotivo?: 'impedimento' | 'requisitos' | 'nao_confirmado' | null;
  /** Observação da IN 403-1 7.2.8.6.3 (piso/teto), calculada no mapper. */
  observacaoStatus?: string | null;
}

interface ExtratoTotais {
  bruto: string;
  ir: string;
  liquido: string;
}

interface ExtratoProps {
  iniciativas: ExtratoIniciativa[];
  totais: ExtratoTotais;
  observacao?: string;
}

const VARIANT_LABEL: Record<ExtratoVariant, string> = {
  'classificada-etapa1': `CLASSIFICADA ETAPA 1 ${SUFIXO_PREMIADA}`,
  'classificada-etapa2': `CLASSIFICADA ETAPA 2 ${SUFIXO_PREMIADA}`,
  'podio-1': `1º LUGAR ${SUFIXO_PREMIADA}`,
  'podio-2': `2º LUGAR ${SUFIXO_PREMIADA}`,
  'podio-3': `3º LUGAR ${SUFIXO_PREMIADA}`,
};

/** Textos dos motivos de status negativo conforme IN 403-1 */
const STATUS_NEGATIVO_TEXT: Record<'impedimento' | 'requisitos' | 'nao_confirmado', string> = {
  impedimento:
    'Não atendeu aos itens de verificação de impedimentos IN 403-1 item 1.6.1',
  requisitos:
    'Não cumpriu requisitos: Não atendeu a todos os requisitos listados na IN 403-1 item 7.2.8.2',
  nao_confirmado:
    'Não confirmado: Não confirmou participação no Prêmio e Adesão ao Regulamento conforme IN 403-1 item 7.2.6.2',
};

const isPodio = (variant: ExtratoVariant) => variant.startsWith('podio-');

export const Extrato: React.FC<ExtratoProps> = ({
  iniciativas,
  totais,
  observacao = 'OBS: O valor depositado corresponde ao valor líquido do prêmio, já com o desconto da alíquota de imposto de renda aplicável.',
}) => {
  return (
    <section className="extrato" aria-labelledby="extrato-title">
      <h2 className="extrato__title" id="extrato-title" tabIndex={0}>
        Extrato das Classificadas Etapa 1
      </h2>

      <div className="extrato__container">
        {iniciativas.map((iniciativa) => {
          const textoStatusNegativo =
            iniciativa.variant === 'classificada-etapa1' &&
            iniciativa.statusMotivo
              ? STATUS_NEGATIVO_TEXT[iniciativa.statusMotivo] ?? null
              : null;

          const exibirObservacaoStatus =
            iniciativa.variant === 'classificada-etapa1' &&
            !!iniciativa.observacaoStatus;

          return (
            <article
              key={iniciativa.id}
              className={`extrato-card extrato-card--${iniciativa.variant}`}
              aria-label={`${VARIANT_LABEL[iniciativa.variant]} — ${iniciativa.titulo}`}
            >
              <header className="extrato-card__frame">
                <span className="extrato-card__frame-label">
                  {isPodio(iniciativa.variant) ? (
                    <img
                      src={logoPodio}
                      alt=""
                      aria-hidden="true"
                      className="extrato-card__frame-icon extrato-card__frame-icon--podio"
                    />
                  ) : (
                    <FaTrophy
                      size={18}
                      aria-hidden="true"
                      className="extrato-card__frame-icon"
                    />
                  )}
                  {VARIANT_LABEL[iniciativa.variant]}
                </span>
                <span className="extrato-card__category-pill">
                  {iniciativa.categoriaLabel}
                </span>
              </header>

              <div className="extrato-card__body">
                <h3 className="extrato-card__iniciativa-title">{iniciativa.titulo}</h3>

                <p className="extrato-card__section-label">Detalhes do Prêmio</p>

                <div
                  className="extrato-card__valores"
                  role="list"
                  aria-label="Detalhes do prêmio"
                >
                  {iniciativa.valores.map((valor, index) => (
                    <div
                      key={`${iniciativa.id}-valor-${index}`}
                      className="extrato-card__valor-item"
                      role="listitem"
                    >
                      <span className="extrato-card__valor-rotulo">{valor.rotulo}</span>
                      <span className="extrato-card__valor-valor">{valor.valor}</span>
                    </div>
                  ))}
                </div>

                {iniciativa.participacaoConfirmada ? (
                  <p className="extrato-card__status">
                    <IoCheckmarkCircle size={16} aria-hidden="true" />
                    Confirmada a participação
                  </p>
                ) : textoStatusNegativo ? (
                  <p className="extrato-card__status extrato-card__status--negativo">
                    <IoCloseCircle size={16} aria-hidden="true" />
                    {textoStatusNegativo}
                  </p>
                ) : null}

                {exibirObservacaoStatus && (
                  <p className="extrato-card__status-obs">
                    {iniciativa.observacaoStatus}
                  </p>
                )}
              </div>
            </article>
          );
        })}

        <footer className="extrato__footer">
          <div className="extrato__totais" aria-label="Totais do extrato">
            <div className="extrato__totais-item">
              <span className="extrato__totais-label">Valor total bruto</span>
              <span className="extrato__totais-valor" aria-live="polite">
                {totais.bruto}
              </span>
            </div>
            <div className="extrato__totais-item">
              <span className="extrato__totais-label">Desconto de IR</span>
              <span className="extrato__totais-valor" aria-live="polite">
                {totais.ir}
              </span>
            </div>
            <div className="extrato__totais-item">
              <span className="extrato__totais-label">Valor total líquido</span>
              <span className="extrato__totais-valor" aria-live="polite">
                {totais.liquido}
              </span>
            </div>
          </div>
          <p className="extrato__observacao">{observacao}</p>
        </footer>
      </div>
    </section>
  );
};
